import { useMemo, useState, type FC, type ReactNode } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

/**
 * Waits for the next paint frame — used to let a just-triggered React state
 * update (the PMA-COCK-160 offscreen Area Control export mount) commit and
 * paint before html2canvas captures it. Falls back to a macrotask tick in
 * environments without `requestAnimationFrame` (e.g. jsdom under vitest).
 */
const waitForNextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    } else {
      setTimeout(resolve, 0);
    }
  });
/** Brand-logo aspect ratio (viewBox 0 0 501 395.25) */
const BRAND_LOGO_ASPECT = 395.25 / 501;
const BRAND_LOGO_URL = "/logos/promatch-brand.svg";

/** Load the brand SVG as an HTMLImageElement (cached after first call). */
let _cachedLogoImg: HTMLImageElement | null = null;
const loadBrandLogo = (): Promise<HTMLImageElement> => {
  if (_cachedLogoImg) return Promise.resolve(_cachedLogoImg);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      _cachedLogoImg = img;
      resolve(img);
    };
    img.onerror = reject;
    img.src = BRAND_LOGO_URL;
  });
};
import { MatchEvent } from "../../../../store/useMatchLogStore";
import { Match, AdvancedMetrics, getCardSubjectId } from "../../types";
import TeamLogo from "../../../../components/TeamLogo";
import { formatDisplayName } from "../../../../lib/nameFormat";
import {
  computeIneffectiveBreakdown,
  computeTimerFormulas,
  computePossessionSplit,
  IneffectiveBreakdown,
  IneffectiveAction,
} from "../../utils";
import { computeIneffectiveActionRows } from "../../utils/ineffectiveRows";
import {
  getRenderedPeriods,
  PERIOD_MAPS_LABEL_KEYS,
} from "./PeriodMapsSection";
import { TrendingUp, Activity, Info } from "../../../../components/icons";
import { MatchMomentumChart } from "../../analytics/momentum/MatchMomentumChart";
import { BallRecoveryTimeBanner } from "../../analytics/momentum/BallRecoveryTimeBanner";
import { resolveMatchTeamColors } from "./resolveMatchTeamColors";
import { computeAreaControlData } from "../../utils/heatMapZones";
import AreaControlGrid from "./AreaControlGrid";
import TeamControlMap from "./TeamControlMap";
import GoalMouthMap from "./GoalMouthMap";
import SectionGate from "../../../../components/match/SectionGate";
import TopPlayersSection from "./TopPlayersSection";
import PlayerCardPicker from "./PlayerCardPicker";
import { usePlayerStats } from "../../hooks/usePlayerStats";
import { rankTopPlayers } from "../../utils/topPlayers";
import { computeMinutesPlayed } from "../../utils/minutesPlayed";
import { isShootoutKick } from "../../utils/penaltyShootout";
import { sameTeamId } from "../../utils/teamId";
import type {
  ShareSectionKey,
  ShareUpsell,
  ShareVisibilityState,
} from "../../../../lib/shareVisibility";

interface MatchAnalyticsProps {
  match: Match | null;
  events: MatchEvent[];
  effectiveTime: number;
  varTimeSeconds?: number;
  timeoutSeconds?: number;
  ineffectiveSeconds?: number;
  ineffectiveBreakdown?: IneffectiveBreakdown | null;
  advancedMetrics?: AdvancedMetrics | null;
  /**
   * Accepted for API-contract parity with the sibling `HeatMapSection`
   * component (both are rendered independently by `AnalyticsView` /
   * `PublicMatchAnalytics`, not nested). `MatchAnalytics` itself renders no
   * heat maps, so this prop is a no-op here — callers must pass the same
   * value to `HeatMapSection` directly to actually hide per-player heat maps.
   */
  hidePlayerHeatmaps?: boolean;
  /**
   * Hides the export/total-events header row (Total Events pill, realtime
   * caption, Export PDF button) while keeping the section title
   * (`analytics-title`) visible. Default `false` (cockpit AnalyticsView is
   * unaffected). Used by the public share page, which has no export controls.
   */
  hideExportControls?: boolean;
  /**
   * Hides the "Per-Team Time Detail" table while leaving the comparison
   * table and ineffective-time breakdown visible. Default `false` (cockpit
   * AnalyticsView is unaffected). Used by the public share page.
   */
  hidePerTeamTimeTable?: boolean;
  /**
   * PMA-PUB-014 (EPIC-27) — the public share page's per-section visibility
   * lookup. Gates `stats_comparison` (possession donut + comparison table),
   * `shot_map` (goal-mouth section), `ineffective_breakdown`,
   * `recovery_banner` and `momentum_chart`, each in its own `SectionGate`.
   * Absent (the cockpit's own `AnalyticsView` mount never passes it) means
   * every section renders exactly as before this feature — no `SectionGate`
   * wrapper at all, DOM unchanged.
   */
  /**
   * EPIC-107 D1/D4: the live match clock in seconds (cockpit global clock /
   * share page global clock). Ends open stints in the Top 3 minutes. When
   * absent, the latest non-shootout event clock is used instead.
   */
  currentClockSeconds?: number;
  /**
   * EPIC-110 D7: mount the single-player card picker (`public-player-card-section`)
   * right below the Top 3 section, gated by the `player_card` share key. Only the
   * share page passes it; the cockpit mounts the picker in `AnalyticsView`.
   */
  showPlayerCard?: boolean;
  /** Period (1-4) `currentClockSeconds` belongs to; lifts stoppage when that period has no events yet. */
  currentPeriod?: number;
  /** `currentClockSeconds` is already one continuous total (COMPLETED/PENALTIES): never lift it. */
  matchFinished?: boolean;
  sectionState?: (key: ShareSectionKey) => ShareVisibilityState;
  upsell?: ShareUpsell;
  language?: "en" | "es";
  t: any;
}

interface EventTimeline {
  minute: number;
  home: number;
  away: number;
}

interface EventTypeCount {
  type: string;
  count: number;
  [key: string]: any;
}

interface TeamComparison {
  metric: string;
  home: number;
  away: number;
}

interface PlayerActivity {
  player_id: string;
  player_name: string;
  events: number;
  passes: number;
  shots: number;
}

interface ComparativeRow {
  label: string;
  home: string | number;
  away: string | number;
  testId: string;
  tooltip?: string;
  /**
   * Extra attributes spread onto the row's container div — e.g. a
   * machine-readable availability flag (`data-xg-state`) alongside a
   * human-readable placeholder value, so a "no data yet" row is
   * distinguishable from a genuine zero without relying on a display glyph.
   */
  dataAttrs?: Record<string, string>;
  /**
   * EPIC-107 D3: per-team yellow/red card counts rendered as badges next to
   * the row value (Fouls row only). Also appended to the PDF export cell.
   */
  cardBadges?: {
    home: { yellow: number; red: number };
    away: { yellow: number; red: number };
  };
}

/**
 * EPIC-107 D3: small square card badge in the comparison table's Fouls row.
 * Yellow: dark text on yellow-400; red: white text on red-600 (both AA).
 */
const CardBadge: FC<{
  kind: "yellow" | "red";
  side: "home" | "away";
  count: number;
  label: string;
}> = ({ kind, side, count, label }) => (
  <span
    data-testid={`stat-fouls-${kind}-${side}`}
    aria-label={`${label}: ${count}`}
    role="img"
    className={`inline-flex h-5 min-w-5 items-center justify-center rounded-sm px-1 text-xs font-bold leading-none md:h-7 md:min-w-7 md:text-sm ${
      kind === "yellow"
        ? "bg-yellow-400 text-slate-900"
        : "bg-red-600 text-white"
    }`}
  >
    {count}
  </span>
);

interface TeamCardTotals {
  yellow: number;
  red: number;
}

/**
 * R4 contrast fix — "Análisis Público del Partido" stats panel.
 *
 * The panel previously used a `from-emerald-900 via-slate-900 to-slate-900`
 * gradient. Every headline stat number already cleared WCAG AA against it,
 * but several secondary elements did not (measured against the actual local
 * background each sits on, not just the outer gradient stop):
 *   - the "N Neutral" sub-annotation (was `text-slate-500`): ~2.0-3.1:1
 *   - the info-tooltip glyph (was `text-emerald-400/60`): ~2.0-2.2:1
 * Below are TWO full replacement schemes — every text/number role in this
 * panel is re-verified at >=4.5:1 (normal text) / >=3:1 (large text and the
 * decorative tooltip glyph/donut track). See team_findings.md for the full
 * ratio table (per role, against every background it can appear on) and the
 * exact contract changelog text. Do NOT pick one unilaterally — both are
 * wired up so flipping `ACTIVE_STATS_THEME` below previews the other option;
 * the lead confirms the final pick with the client.
 */
interface StatsPanelTheme {
  /** Outer panel container background + default/inherited text color. */
  panel: string;
  /** Nested comparative / per-team-time row background. */
  row: string;
  /** Uppercase accent labels (Total Time, VS, Metric header, row labels). */
  label: string;
  /** Headline stat values inside comparative / per-team-time rows. */
  value: string;
  /** "Tiempo perdido" values, team name, donut % (inherited base text). */
  valueAlt: string;
  /** Center sub-labels, tiny column headers, "N Neutral" annotations. */
  subtle: string;
  /** Info-tooltip glyph color. */
  icon: string;
  /** Grouped "tiempo perdido" box background. */
  box: string;
  /** Divider border color (box rows + standalone rows + total-row top). */
  border: string;
  /** Highlighted totals-row background. */
  totalRow: string;
  /** Possession-donut inner cutout background. */
  donutInset: string;
  /** Possession-donut "remaining" track color (2nd conic-gradient stop). */
  donutTrack: string;
}

// Option 1 — "Deep Slate": keeps a dark aesthetic, but swaps the muddy
// green-into-slate gradient for a neutral near-black canvas so emerald/white
// text and numbers pop instead of blending into a green-on-green wash.
// Worst-case measured ratio: 9.02:1 (label/subtle/icon vs. the emerald total
// row) — every role clears AA with wide margin.
export const STATS_THEME_DARK: StatsPanelTheme = {
  panel: "bg-slate-950 text-white",
  row: "bg-white/8",
  label: "text-emerald-300",
  value: "text-emerald-50",
  valueAlt: "text-white",
  subtle: "text-slate-300",
  icon: "text-emerald-300",
  box: "bg-black",
  border: "border-slate-700",
  totalRow: "bg-emerald-500/25",
  donutInset: "bg-slate-900",
  donutTrack: "rgba(255,255,255,0.12)",
};

// Option 2 — "Light Card": light background, dark text. A bigger visual
// departure from the current dark cockpit, but the client's core complaint
// was that the data itself is hard to read, and dark text on a light card is
// the highest-legibility option available. Worst-case measured ratio:
// 4.84:1 (icon vs. the emerald total row) — every role clears AA.
export const STATS_THEME_LIGHT: StatsPanelTheme = {
  panel:
    "bg-gradient-to-br from-slate-50 via-white to-emerald-50 text-slate-900",
  row: "bg-slate-100",
  label: "text-emerald-800",
  value: "text-slate-900",
  valueAlt: "text-slate-900",
  subtle: "text-slate-600",
  icon: "text-emerald-700",
  box: "bg-white border border-slate-200",
  border: "border-slate-200",
  totalRow: "bg-emerald-100",
  donutInset: "bg-white border border-slate-200",
  donutTrack: "rgba(15,23,42,0.12)",
};

// ACTIVE: flip to STATS_THEME_LIGHT to preview Option 2. Neither is final —
// both are pending client confirmation (see team_findings.md).
//
// PMA-COCK-189: `IneffectiveBreakdownSection.tsx` duplicates this value's
// STATS_THEME_DARK fields rather than importing it — several existing
// tests `vi.mock` this whole module down to a bare stub, and a real import
// from it would crash the moment that component renders unmocked
// underneath. If this ever flips to STATS_THEME_LIGHT, update the literal
// copy there too.
const ACTIVE_STATS_THEME: StatsPanelTheme = STATS_THEME_DARK;

const formatSecondsAsClock = (seconds: number) => {
  // Defense in depth: any negative input (malformed upstream data, a future
  // arithmetic regression) degrades to 00:00 instead of a negative-looking
  // clock like "-1:-1" — Math.floor/`%` on a negative dividend both keep
  // the sign, and a single "-1" is already 2 chars so padStart is a no-op.
  // Math.max(0, NaN) is NaN, not 0, so finiteness is guarded separately —
  // otherwise a NaN input (e.g. 0/0 upstream) would render "NaN:NaN".
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const mins = Math.floor(safeSeconds / 60);
  const secs = Math.floor(safeSeconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const parseClockToSeconds = (clock?: string) => {
  if (!clock) return 0;
  const [mm, rest] = clock.split(":");
  const seconds = parseFloat(rest || "0");
  return Number(mm) * 60 + seconds;
};

const compareCardEventOrder = (
  left: { event: MatchEvent; index: number },
  right: { event: MatchEvent; index: number },
) => {
  const leftPeriod = Number(left.event.period || 0);
  const rightPeriod = Number(right.event.period || 0);
  if (leftPeriod !== rightPeriod) return leftPeriod - rightPeriod;

  const leftTs = Date.parse(left.event.timestamp || "");
  const rightTs = Date.parse(right.event.timestamp || "");
  const leftHasTs = Number.isFinite(leftTs);
  const rightHasTs = Number.isFinite(rightTs);
  if (leftHasTs && rightHasTs && leftTs !== rightTs) return leftTs - rightTs;

  const leftClock = parseClockToSeconds(left.event.match_clock);
  const rightClock = parseClockToSeconds(right.event.match_clock);
  if (leftClock !== rightClock) return leftClock - rightClock;

  return left.index - right.index;
};

const getNetCardTotalsByTeam = (
  events: MatchEvent[],
  homeTeamId: string,
  awayTeamId: string,
) => {
  // Keyed by card SUBJECT, not by player: a booked coach or assistant carries
  // `staff_id` instead of `player_id` (PMA-COCK-095) and their cards count
  // toward the team total exactly like a player's — the backend team totals in
  // `player_stats_aggregates.py` already do this.
  const stateBySubject = new Map<
    string,
    {
      teamId: string;
      yellow: number;
      red: number;
      suppressNextRed: number;
    }
  >();

  const orderedCards = events
    .map((event, index) => ({ event, index }))
    .filter(
      ({ event }) => event.type === "Card" && Boolean(getCardSubjectId(event)),
    )
    .sort(compareCardEventOrder);

  orderedCards.forEach(({ event }) => {
    const subjectId = getCardSubjectId(event);
    if (!subjectId) return;
    const current = stateBySubject.get(subjectId) ?? {
      teamId: event.team_id,
      yellow: 0,
      red: 0,
      suppressNextRed: 0,
    };
    current.teamId = event.team_id || current.teamId;

    const cardType = String(event.data?.card_type || "").toLowerCase();

    if (cardType.includes("cancel")) {
      if (current.red > 0 && current.yellow >= 2) {
        current.red -= 1;
        current.yellow -= 1;
      } else if (current.red > 0) {
        current.red -= 1;
      } else if (current.yellow > 0) {
        current.yellow -= 1;
      }
      stateBySubject.set(subjectId, current);
      return;
    }

    if (cardType.includes("yellow (second)")) {
      current.yellow += 1;
      current.red += 1;
      current.suppressNextRed += 1;
      stateBySubject.set(subjectId, current);
      return;
    }

    if (cardType.includes("yellow")) {
      current.yellow += 1;
      stateBySubject.set(subjectId, current);
      return;
    }

    if (cardType.includes("red")) {
      if (current.suppressNextRed > 0) {
        current.suppressNextRed -= 1;
      } else {
        current.red += 1;
      }
      stateBySubject.set(subjectId, current);
      return;
    }
  });

  const totalsByTeam: Record<string, TeamCardTotals> = {
    [homeTeamId]: { yellow: 0, red: 0 },
    [awayTeamId]: { yellow: 0, red: 0 },
  };

  stateBySubject.forEach(({ teamId, yellow, red }) => {
    if (!totalsByTeam[teamId]) {
      totalsByTeam[teamId] = { yellow: 0, red: 0 };
    }
    totalsByTeam[teamId].yellow += yellow;
    totalsByTeam[teamId].red += red;
  });

  return totalsByTeam;
};

export function MatchAnalytics({
  match,
  events,
  effectiveTime,
  varTimeSeconds = 0,
  timeoutSeconds = 0,
  ineffectiveSeconds = 0,
  ineffectiveBreakdown,
  advancedMetrics,
  hideExportControls = false,
  hidePerTeamTimeTable = false,
  hidePlayerHeatmaps = false,
  sectionState,
  upsell,
  language,
  currentClockSeconds,
  showPlayerCard = false,
  currentPeriod,
  matchFinished = false,
  t,
}: MatchAnalyticsProps) {
  // PMA-PUB-014: wraps `node` in a `SectionGate` only when the caller (the
  // public page) passed `sectionState` — the cockpit's own `AnalyticsView`
  // mount never does, so `gate` is a no-op identity function there and the
  // DOM is unchanged.
  const gate = (key: ShareSectionKey, node: ReactNode): ReactNode => {
    if (!sectionState || !upsell || !language) return node;
    return (
      <SectionGate
        sectionKey={key}
        state={sectionState(key)}
        upsell={upsell}
        language={language}
      >
        {node}
      </SectionGate>
    );
  };
  // EPIC-107 D4: Top 3 per team. Minutes come from the client util (never the
  // backend field); the open-stint end is the latest non-shootout event clock.
  // Rosterless matches (partial fixtures, a not-yet-loaded roster) simply
  // render no Top 3 instead of crashing `usePlayerStats`.
  const rosterReady =
    Array.isArray(match?.home_team?.players) &&
    Array.isArray(match?.away_team?.players);
  const playerStats = usePlayerStats(rosterReady ? match : null, events);
  const topPlayersByTeam = useMemo(() => {
    if (!match || !rosterReady) return null;
    // Stamped clocks restart each period and keep stoppage, so the "last
    // event clock" is the max clock within the LATEST period only (a
    // first-half 47:00 must not beat a second-half 46:00). Same broadcast
    // frame as `currentClockSeconds`.
    let clock = 0;
    let clockPeriod = 0;
    for (const e of events) {
      if (isShootoutKick(e)) continue;
      const sec = parseClockToSeconds(e.match_clock);
      if (!Number.isFinite(sec)) continue;
      const p = typeof e.period === "number" ? e.period : 1;
      if (p > clockPeriod || (p === clockPeriod && sec > clock)) {
        clockPeriod = p;
        clock = sec;
      }
    }
    if (
      typeof currentClockSeconds === "number" &&
      Number.isFinite(currentClockSeconds) &&
      !matchFinished &&
      // A later current period wins outright (its clock restarts); within
      // the same period keep the max.
      ((typeof currentPeriod === "number" && currentPeriod > clockPeriod) ||
        currentClockSeconds > clock)
    ) {
      clock = currentClockSeconds;
    }
    const minutes = computeMinutesPlayed(
      events,
      { home: match.home_team.players, away: match.away_team.players },
      clock,
      currentPeriod,
      {
        matchFinished,
        matchTotalSeconds:
          (match.match_time_seconds ?? 0) +
          (match.ineffective_time_seconds ?? 0),
        periodTimestamps: match.period_timestamps,
      },
    );
    return {
      home: rankTopPlayers(playerStats, minutes, "home"),
      away: rankTopPlayers(playerStats, minutes, "away"),
    };
  }, [
    match,
    events,
    playerStats,
    rosterReady,
    currentClockSeconds,
    currentPeriod,
    matchFinished,
  ]);
  // Calculate analytics data
  const analytics = useMemo(() => {
    if (!match || events.length === 0) {
      return null;
    }

    const homeTeamId = match.home_team.id;
    const awayTeamId = match.away_team.id;
    const breakdown =
      ineffectiveBreakdown ||
      computeIneffectiveBreakdown(events, homeTeamId, awayTeamId, Date.now());

    const ineffectiveTotals = breakdown?.totals ?? {
      home: 0,
      away: 0,
      neutral: 0,
      byAction: {} as Record<
        IneffectiveAction,
        { home: number; away: number; neutral: number }
      >,
    };

    const summedTeamIneffective = Object.entries(
      ineffectiveTotals.byAction || {},
    ).reduce(
      (acc, [action, totals]) => {
        if (action === "VAR") return acc;
        acc.home += totals?.home ?? 0;
        acc.away += totals?.away ?? 0;
        return acc;
      },
      { home: 0, away: 0 },
    );

    const teamIneffectiveTotals = {
      home:
        summedTeamIneffective.home > 0 || summedTeamIneffective.away > 0
          ? summedTeamIneffective.home
          : ineffectiveTotals.home,
      away:
        summedTeamIneffective.home > 0 || summedTeamIneffective.away > 0
          ? summedTeamIneffective.away
          : ineffectiveTotals.away,
    };

    // Event timeline (events per 5-minute interval)
    const timelineMap = new Map<number, { home: number; away: number }>();
    const maxMinute = Math.ceil(effectiveTime / 60);

    for (let i = 0; i <= maxMinute; i += 5) {
      timelineMap.set(i, { home: 0, away: 0 });
    }

    events.forEach((event) => {
      const [minutes] = event.match_clock.split(":").map(Number);
      const interval = Math.floor(minutes / 5) * 5;
      const entry = timelineMap.get(interval);
      if (entry) {
        if (sameTeamId(event.team_id, homeTeamId)) {
          entry.home++;
        } else if (sameTeamId(event.team_id, awayTeamId)) {
          entry.away++;
        }
      }
    });

    const timeline: EventTimeline[] = Array.from(timelineMap.entries())
      .map(([minute, counts]) => ({
        minute,
        home: counts.home,
        away: counts.away,
      }))
      .filter(
        (_, idx, arr) =>
          idx === 0 ||
          idx === arr.length - 1 ||
          timelineMap.get((_ as any).minute)!.home +
            timelineMap.get((_ as any).minute)!.away >
            0,
      );

    // Event type distribution
    // "Throw-in" is excluded here: dead time from throw-ins is already tracked
    // in ineffectiveActionRows (ThrowIn bucket). Including it in the raw event
    // count would produce two throw-in rows in the exported PDF.
    const eventTypeMap = new Map<string, number>();
    events.forEach((event) => {
      if (event.type === "Throw-in") return;
      eventTypeMap.set(event.type, (eventTypeMap.get(event.type) || 0) + 1);
    });

    const eventTypes: EventTypeCount[] = Array.from(eventTypeMap.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // Team comparison metrics
    const homeEvents = events.filter((e) => sameTeamId(e.team_id, homeTeamId));
    const awayEvents = events.filter((e) => sameTeamId(e.team_id, awayTeamId));

    const getEventCount = (teamEvents: MatchEvent[], type: string) =>
      teamEvents.filter((e) => e.type === type).length;

    const getShotOutcomeCount = (
      teamEvents: MatchEvent[],
      outcomes: string[],
      excludeOwnGoals = false,
    ) =>
      teamEvents.filter(
        (e) =>
          e.type === "Shot" &&
          outcomes.includes(String(e.data?.outcome || "")) &&
          (!excludeOwnGoals || !e.data?.own_goal),
      ).length;

    const getPassCounts = (teamEvents: MatchEvent[]) => {
      const total = teamEvents.filter((e) => e.type === "Pass").length;
      const accurate = teamEvents.filter(
        (e) => e.type === "Pass" && e.data?.outcome === "Complete",
      ).length;
      return { total, accurate };
    };

    const cardTotalsByTeam = getNetCardTotalsByTeam(
      events,
      homeTeamId,
      awayTeamId,
    );

    const homePasses = getPassCounts(homeEvents);
    const awayPasses = getPassCounts(awayEvents);
    // Exclude own goals from shot statistics (own goals inflate the opponent's
    // shot counts because team_id is set to the opponent).
    const homeShots = homeEvents.filter(
      (e) => e.type === "Shot" && !e.data?.own_goal,
    ).length;
    const awayShots = awayEvents.filter(
      (e) => e.type === "Shot" && !e.data?.own_goal,
    ).length;
    const homeShotsOnTarget = getShotOutcomeCount(
      homeEvents,
      ["OnTarget", "Saved", "Goal"],
      true,
    );
    const awayShotsOnTarget = getShotOutcomeCount(
      awayEvents,
      ["OnTarget", "Saved", "Goal"],
      true,
    );
    const homeShotsOffTarget = getShotOutcomeCount(
      homeEvents,
      ["OffTarget", "Post"],
      true,
    );
    const awayShotsOffTarget = getShotOutcomeCount(
      awayEvents,
      ["OffTarget", "Post"],
      true,
    );
    // PMA-COCK-165: blocked shots were previously counted in neither the
    // on- nor off-target rows. `homeShots`/`awayShots` (Total) already
    // include every non-own-goal Shot regardless of outcome, so Blocked was
    // always in the Total — just missing its own breakdown row.
    const homeShotsBlocked = getShotOutcomeCount(homeEvents, ["Blocked"], true);
    const awayShotsBlocked = getShotOutcomeCount(awayEvents, ["Blocked"], true);
    const homeGoals = getShotOutcomeCount(homeEvents, ["Goal"]);
    const awayGoals = getShotOutcomeCount(awayEvents, ["Goal"]);
    const homeScore = homeGoals > 0 ? homeGoals : match.home_team.score || 0;
    const awayScore = awayGoals > 0 ? awayGoals : match.away_team.score || 0;
    const homeCorners = homeEvents.filter(
      (e) => e.type === "SetPiece" && e.data?.set_piece_type === "Corner",
    ).length;
    const awayCorners = awayEvents.filter(
      (e) => e.type === "SetPiece" && e.data?.set_piece_type === "Corner",
    ).length;
    const homeOffsides =
      getEventCount(homeEvents, "Offside") +
      homeEvents.filter(
        (e) => e.type === "Pass" && e.data?.outcome === "Pass Offside",
      ).length;
    const awayOffsides =
      getEventCount(awayEvents, "Offside") +
      awayEvents.filter(
        (e) => e.type === "Pass" && e.data?.outcome === "Pass Offside",
      ).length;
    const homeFouls = getEventCount(homeEvents, "FoulCommitted");
    const awayFouls = getEventCount(awayEvents, "FoulCommitted");
    const homeYellows = cardTotalsByTeam[homeTeamId]?.yellow ?? 0;
    const awayYellows = cardTotalsByTeam[awayTeamId]?.yellow ?? 0;
    const homeReds = cardTotalsByTeam[homeTeamId]?.red ?? 0;
    const awayReds = cardTotalsByTeam[awayTeamId]?.red ?? 0;

    const teamComparison: TeamComparison[] = [
      {
        metric: t("analytics.passes", "Passes"),
        home: getEventCount(homeEvents, "Pass"),
        away: getEventCount(awayEvents, "Pass"),
      },
      {
        metric: t("analytics.shots", "Shots"),
        home: getEventCount(homeEvents, "Shot"),
        away: getEventCount(awayEvents, "Shot"),
      },
      {
        metric: t("analytics.duels", "Duels"),
        home: getEventCount(homeEvents, "Duel"),
        away: getEventCount(awayEvents, "Duel"),
      },
      {
        metric: t("analytics.fouls", "Fouls"),
        home: getEventCount(homeEvents, "FoulCommitted"),
        away: getEventCount(awayEvents, "FoulCommitted"),
      },
      {
        metric: t("analytics.interceptions", "Interceptions"),
        home: getEventCount(homeEvents, "Interception"),
        away: getEventCount(awayEvents, "Interception"),
      },
    ];

    // Player activity (top 6 most active players)
    const playerMap = new Map<string, PlayerActivity>();

    events.forEach((event) => {
      if (event.player_id) {
        if (!playerMap.has(event.player_id)) {
          const team = sameTeamId(event.team_id, homeTeamId)
            ? match.home_team
            : match.away_team;
          const player = team.players.find((p) => p.id === event.player_id);
          playerMap.set(event.player_id, {
            player_id: event.player_id,
            player_name:
              player?.short_name ||
              formatDisplayName(player?.full_name) ||
              `#${event.player_id}`,
            events: 0,
            passes: 0,
            shots: 0,
          });
        }
        const activity = playerMap.get(event.player_id)!;
        activity.events++;
        if (event.type === "Pass") activity.passes++;
        if (event.type === "Shot") activity.shots++;
      }
    });

    const topPlayers = Array.from(playerMap.values())
      .sort((a, b) => b.events - a.events)
      .slice(0, 6);

    // Possession approximation from each team's share of logged passes.
    // home% + away% always sums to exactly 100 (EPIC-110 D1); duels no
    // longer enter the split.
    const homeDuels = getEventCount(homeEvents, "Duel");
    const awayDuels = getEventCount(awayEvents, "Duel");
    const { homePossession, awayPossession, contestedPossession } =
      computePossessionSplit({
        homeWeight: homePasses.total,
        awayWeight: awayPasses.total,
        contestedWeight: homeDuels + awayDuels,
      });

    // Key stats
    const totalEvents = events.length;
    const avgEventsPerMinute =
      maxMinute > 0 ? (totalEvents / maxMinute).toFixed(1) : "0.0";
    // Global time matches useMatchTimer: effective + ineffective + timeout (VAR excluded, shown separately)
    const {
      globalSeconds,
      homeEffectiveSeconds,
      awayEffectiveSeconds,
      homeEffectivePercent,
      awayEffectivePercent,
      homeIneffectivePercent,
      awayIneffectivePercent,
    } = computeTimerFormulas({
      effectiveTime,
      ineffectiveSeconds,
      timeoutSeconds,
      varTimeSeconds,
      teamIneffective: teamIneffectiveTotals,
      possession: { home: homePossession, away: awayPossession },
    });
    const totalMatchSeconds = globalSeconds;
    const mostActiveMinute = Array.from(timelineMap.entries()).reduce(
      (max, [min, counts]) => {
        const total = counts.home + counts.away;
        return total > max.count ? { minute: min, count: total } : max;
      },
      { minute: 0, count: 0 },
    );

    // xG comparison row (Phase 2d, PMA-UI wave): `generated_at` is the
    // established availability gate this codebase already uses for the
    // same `AdvancedMetrics` document (cockpit's own "xG Race" panel,
    // the public page's "Team Advanced Metrics" summary) — null covers
    // both "not yet computed" and "fetch failed" identically, since the
    // UI has no way to tell those apart and one row shouldn't try to.
    const xgAvailable = advancedMetrics?.generated_at != null;

    return {
      timeline,
      eventTypes,
      teamComparison,
      topPlayers,
      homePossession,
      awayPossession,
      contestedPossession,
      // PMA-COCK-189: the per-action rows now come from the shared
      // `computeIneffectiveActionRows` helper (also used by the relocated
      // `IneffectiveBreakdownSection` and, below, this file's own PDF
      // export table) so both surfaces read the identical numbers for the
      // same `breakdown`. The old byAction-summed `ineffectiveTotals`
      // override (home/away Total-row figures) moved out with the "Tiempo
      // perdido" JSX block itself — `IneffectiveBreakdownSection` computes
      // it independently via `computeIneffectiveTotals`, and nothing left
      // in this file reads it (the `perTeamTimeRows` "Ineffective Time" row
      // above uses the un-overridden local `ineffectiveTotals` instead).
      ineffectiveActionRows: computeIneffectiveActionRows(breakdown, t),
      comparativeRows: [
        {
          label: t("analytics.xg", "Expected Goals (xG)"),
          home: xgAvailable
            ? advancedMetrics!.team_totals.home.xg.toFixed(2)
            : "—",
          away: xgAvailable
            ? advancedMetrics!.team_totals.away.xg.toFixed(2)
            : "—",
          testId: "stat-xg",
          tooltip: t(
            "analytics.tooltipXg",
            "Expected Goals — the cumulative quality of scoring chances created by each team, computed live as shot events are logged. Shown as — until the first metrics pass has run for this match.",
          ),
          dataAttrs: { "data-xg-state": xgAvailable ? "available" : "pending" },
        },
        {
          label: t("analytics.possession", "Possession"),
          home: `${homePossession}%`,
          away: `${awayPossession}%`,
          testId: "stat-possession",
          tooltip: t(
            "analytics.tooltipPossession",
            "Estimated from total completed passes per team, with a neutral share from Duel events. Not a direct time-based measurement.",
          ),
        },
        {
          label: t("analytics.accuratePasses", "Passes (Total/Accurate)"),
          // Client wave 2026-08-23, item 4: was `${total} (${accurate}) – ${pct}%`
          // (e.g. "44(22) - 50%"), which read as cluttered. Only the completed
          // count and success percentage are shown now — the attempts total
          // (homePasses.total/awayPasses.total) is still computed and still
          // drives the percentage, just no longer displayed.
          home: `${homePasses.accurate} - ${
            homePasses.total > 0
              ? Math.round((homePasses.accurate / homePasses.total) * 100)
              : 0
          }%`,
          away: `${awayPasses.accurate} - ${
            awayPasses.total > 0
              ? Math.round((awayPasses.accurate / awayPasses.total) * 100)
              : 0
          }%`,
          testId: "stat-accurate-passes",
          tooltip: t(
            "analytics.tooltipPasses",
            "Total passes attempted and accurate passes completed (good outcome).",
          ),
        },
        {
          // PMA-COCK-159 (client wave 2026-09-01): dedicated key for this
          // row only — `analytics.shots` stays untouched for its other
          // consumers (teamComparison metric below, PlayerAnalyticsView,
          // PlayerStatsTable's "SH" legend).
          label: t("analytics.totalShots", "Total shots"),
          home: homeShots,
          away: awayShots,
          testId: "stat-shots",
        },
        {
          label: t("analytics.shotsOnTarget", "Shots on Target"),
          home: homeShotsOnTarget,
          away: awayShotsOnTarget,
          testId: "stat-shots-on-target",
        },
        {
          label: t("analytics.shotsOffTarget", "Shots off Target"),
          home: homeShotsOffTarget,
          away: awayShotsOffTarget,
          testId: "stat-shots-off-target",
        },
        {
          // PMA-COCK-165: client wave 2026-09-07 — blocked shots now have
          // their own breakdown row (screen + PDF, this array feeds both).
          label: t("analytics.shotsBlocked", "Blocked shots"),
          home: homeShotsBlocked,
          away: awayShotsBlocked,
          testId: "stat-shots-blocked",
        },
        {
          label: t("analytics.corners", "Corners"),
          home: homeCorners,
          away: awayCorners,
          testId: "stat-corners",
        },
        {
          label: t("analytics.offsides", "Offsides"),
          home: homeOffsides,
          away: awayOffsides,
          testId: "stat-offsides",
        },
        {
          label: t("analytics.fouls", "Fouls"),
          home: homeFouls,
          away: awayFouls,
          testId: "stat-fouls",
          // EPIC-107 D3: the standalone yellow/red rows are folded into the
          // Fouls row as per-team badges (always rendered, including 0).
          cardBadges: {
            home: { yellow: homeYellows, red: homeReds },
            away: { yellow: awayYellows, red: awayReds },
          },
        },
      ] as ComparativeRow[],
      perTeamTimeRows: [
        {
          label: t("analytics.ineffectiveTime", "Ineffective Time"),
          home: formatSecondsAsClock(ineffectiveTotals.home),
          away: formatSecondsAsClock(ineffectiveTotals.away),
          testId: "stat-ineffective-time",
          tooltip: t(
            "analytics.tooltipIneffectiveTime",
            "Total clock time attributed to each team's stoppages (goals, fouls, cards, substitutions, out-of-bounds). VAR and injury time are excluded from the per-team split.",
          ),
        },
        {
          label: t("analytics.ineffectiveTimePercent", "Ineffective Time %"),
          home: homeIneffectivePercent,
          away: awayIneffectivePercent,
          testId: "stat-ineffective-time-percent",
          tooltip: t(
            "analytics.tooltipIneffectivePercent",
            "Each team's ineffective time as a percentage of (effective + that team's ineffective) time.",
          ),
        },
        {
          label: t("analytics.effectiveTime", "Effective Time"),
          home: formatSecondsAsClock(homeEffectiveSeconds),
          away: formatSecondsAsClock(awayEffectiveSeconds),
          testId: "stat-effective-time",
          tooltip: t(
            "analytics.tooltipEffectiveTime",
            "Ball-in-play time attributed to each team by possession share; the two teams sum to the global effective time.",
          ),
        },
        {
          label: t("analytics.effectiveTimePercent", "Effective Time %"),
          home: homeEffectivePercent,
          away: awayEffectivePercent,
          testId: "stat-effective-time-percent",
          tooltip: t(
            "analytics.tooltipEffectivePercent",
            "Effective time as a percentage of (effective + that team's ineffective) time.",
          ),
        },
      ] as ComparativeRow[],
      totalMatchSeconds,
      totalEvents,
      avgEventsPerMinute,
      mostActiveMinute: mostActiveMinute.minute,
      homeTotal: homeEvents.length,
      awayTotal: awayEvents.length,
      varTimeSeconds,
      homeScore,
      awayScore,
    };
  }, [
    match,
    events,
    effectiveTime,
    ineffectiveSeconds,
    ineffectiveBreakdown,
    t,
    varTimeSeconds,
    timeoutSeconds,
    advancedMetrics,
  ]);

  // PMA-COCK-160 (client wave 2026-09-01): the Area Control map isn't
  // mounted by the cockpit's default per-player heat-map view (a sibling
  // `HeatMapSection` component, not a child of this one), so the PDF export
  // drives a capture-only offscreen mount of it instead — this flag is only
  // ever true for the brief span of `exportPdf`'s Area Control capture step
  // and never affects the visible cockpit UI.
  const [isCapturingAreaControl, setIsCapturingAreaControl] = useState(false);

  // PMA-COCK-170 (client wave 2026-09-07): same reasoning as
  // isCapturingAreaControl above, for the new "Mapa de control por equipo"
  // PDF section — a dedicated offscreen mount rather than the live
  // `HeatMapSection` embed (which is marked `data-export-hidden` for
  // exactly this reason: the live embed exists purely for the cockpit
  // screen, in both toggle states, and must never also end up baked into
  // the "Field Heat Maps" capture alongside this section's own image).
  const [isCapturingControlMap, setIsCapturingControlMap] = useState(false);

  if (!match || !analytics) {
    return (
      <div
        className="flex items-center justify-center h-64 text-gray-400"
        data-testid="analytics-panel"
      >
        <Activity size={48} className="mr-3" />
        <div className="text-lg">
          {t(
            "analytics.noData",
            "No data available yet. Start logging events!",
          )}
        </div>
      </div>
    );
  }

  // PMA-COCK-186 round-1 judge fix (judge point 4): when the admin hides
  // every section this molecule gates, render nothing at all — previously
  // the header (title, total-events pill, export buttons), the stats card's
  // own themed panel, and the per-team time table (an unrelated,
  // non-gated section, still shown) all stayed on screen empty, which the
  // design's "hidden sections leave no placeholder" rule never anticipated.
  // A mixed state (one hidden, others blurred/visible) is unaffected —
  // `gate`'s own per-section wrapper already keeps the surviving content,
  // exactly what the design wants. Only reachable when the caller passed
  // `sectionState` (the public page); the cockpit's own `AnalyticsView`
  // mount never does, so this branch is always false there.
  //
  // PMA-COCK-189: `ineffective_breakdown` dropped out of this list — the
  // "Tiempo perdido" block it used to gate no longer renders inside this
  // component (moved to the standalone `IneffectiveBreakdownSection`,
  // mounted separately, last, by both call sites). Keeping it here would
  // make this molecule hide its OWN remaining content only when an
  // unrelated section elsewhere on the page also happened to be hidden.
  const GATED_SECTION_KEYS: ShareSectionKey[] = [
    "stats_comparison",
    "shot_map",
    "recovery_banner",
    "momentum_chart",
  ];
  if (
    sectionState &&
    GATED_SECTION_KEYS.every((key) => sectionState(key) === "hidden")
  ) {
    return null;
  }

  /** Draw repeating ProMatch logo watermark across every page of a jsPDF doc */
  const drawPdfWatermark = async (doc: jsPDF) => {
    const pageCount = doc.getNumberOfPages();
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();

    // Render the SVG brand logo onto a small canvas, then export as PNG data-URL
    // PMA-COCK-170 (client wave 2026-09-07): bigger AND more visible, per the
    // client's explicit request — 60pt/0.07 -> 90pt/0.12.
    const logoW = 90;
    const logoH = Math.round(logoW * BRAND_LOGO_ASPECT);

    let logoDataUrl: string;
    try {
      const logoImg = await loadBrandLogo();
      const offscreen = document.createElement("canvas");
      offscreen.width = logoW * 2;
      offscreen.height = logoH * 2;
      const oCtx = offscreen.getContext("2d");
      if (!oCtx) return;
      oCtx.drawImage(logoImg, 0, 0, logoW * 2, logoH * 2);
      logoDataUrl = offscreen.toDataURL("image/png");
    } catch {
      return; // logo unavailable – skip watermark
    }

    const gapX = logoW * 3;
    const gapY = logoH * 3;

    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      for (let ly = gapY / 2; ly < ph; ly += gapY) {
        for (let lx = gapX / 2; lx < pw; lx += gapX) {
          doc.saveGraphicsState();
          const gState = (doc as any).GState
            ? new (doc as any).GState({ opacity: 0.12 })
            : null;
          if (gState) doc.setGState(gState);
          doc.addImage(
            logoDataUrl,
            "PNG",
            lx - logoW / 2,
            ly - logoH / 2,
            logoW,
            logoH,
          );
          doc.restoreGraphicsState();
        }
      }
    }
  };

  const exportPdf = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });
    const marginX = 36;
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 84, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(t("analytics.title", "Live Match Analytics"), marginX, 34);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    // --- Team logos in PDF header ---
    // Team logos are SVG-only (hard cutover); jsPDF's addImage cannot embed
    // SVG directly, so rasterize via an offscreen canvas at 2x the on-page
    // size for crisp print output. Resolves null (logo simply omitted, no
    // crash) when logo_url is absent or isn't an .svg.
    const pdfLogoSize = 18;
    const svgLogoToPngDataUrl = (
      logoUrl: string | null | undefined,
    ): Promise<string | null> => {
      if (!logoUrl || !logoUrl.toLowerCase().endsWith(".svg")) {
        return Promise.resolve(null);
      }
      return new Promise<string | null>((resolve) => {
        const el = new Image();
        el.crossOrigin = "anonymous";
        const renderSize = pdfLogoSize * 2;
        el.onload = () => {
          const c = document.createElement("canvas");
          c.width = renderSize;
          c.height = renderSize;
          const ctx = c.getContext("2d");
          if (ctx) {
            ctx.drawImage(el, 0, 0, renderSize, renderSize);
            resolve(c.toDataURL("image/png"));
          } else {
            resolve(null);
          }
        };
        el.onerror = () => resolve(null);
        el.src = logoUrl;
      });
    };

    const homeLogoUrl = await svgLogoToPngDataUrl(match.home_team.logo_url);
    const awayLogoUrl = await svgLogoToPngDataUrl(match.away_team.logo_url);

    // Header line (PMA-COCK-223 AC6): <crest> <home> <h> - <a> <away> <crest>.
    // The score is bold white 18pt; team names shrink (then truncate) so the
    // whole line stays within `pageWidth - 2 * marginX`. Names sit at baseline
    // y=54 and the score at y=56, below the title (baseline 34) and above the status line (70).
    const lineWidthMax = pageWidth - marginX * 2;
    const crestGap = 6;
    const scoreGap = 10;
    const scoreSize = 18;
    const scoreText = `${analytics.homeScore} - ${analytics.awayScore}`;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(scoreSize);
    const scoreWidth = doc.getTextWidth(scoreText);
    const crestsWidth =
      (homeLogoUrl ? pdfLogoSize + crestGap : 0) +
      (awayLogoUrl ? pdfLogoSize + crestGap : 0);
    const namesBudget = lineWidthMax - scoreWidth - scoreGap * 2 - crestsWidth;
    const homeName = String(match.home_team.name || "");
    const awayName = String(match.away_team.name || "");
    doc.setFont("helvetica", "normal");
    let nameSize = 11;
    const namesWidthAt = (size: number) => {
      doc.setFontSize(size);
      return doc.getTextWidth(homeName) + doc.getTextWidth(awayName);
    };
    while (nameSize > 6 && namesWidthAt(nameSize) > namesBudget) {
      nameSize -= 0.5;
    }
    doc.setFontSize(nameSize);
    const fitName = (name: string, maxW: number) => {
      let out = name;
      while (out.length > 1 && doc.getTextWidth(`${out}...`) > maxW) {
        out = out.slice(0, -1);
      }
      return out === name ? name : `${out}...`;
    };
    let homeW = doc.getTextWidth(homeName);
    let awayW = doc.getTextWidth(awayName);
    let homeDraw = homeName;
    let awayDraw = awayName;
    if (homeW + awayW > namesBudget) {
      // Still too wide at the minimum size: split the budget evenly.
      const each = namesBudget / 2;
      homeDraw = fitName(homeName, each);
      awayDraw = fitName(awayName, each);
      homeW = doc.getTextWidth(homeDraw);
      awayW = doc.getTextWidth(awayDraw);
    }
    const totalW = crestsWidth + homeW + awayW + scoreWidth + scoreGap * 2;
    let cx = marginX + Math.max(0, (lineWidthMax - totalW) / 2);
    if (homeLogoUrl) {
      doc.addImage(homeLogoUrl, "PNG", cx, 40, pdfLogoSize, pdfLogoSize);
      cx += pdfLogoSize + crestGap;
    }
    doc.text(homeDraw, cx, 54);
    cx += homeW + scoreGap;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(scoreSize);
    doc.text(scoreText, cx, 56);
    cx += scoreWidth + scoreGap;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(nameSize);
    doc.text(awayDraw, cx, 54);
    cx += awayW;
    if (awayLogoUrl) {
      doc.addImage(
        awayLogoUrl,
        "PNG",
        cx + crestGap,
        40,
        pdfLogoSize,
        pdfLogoSize,
      );
    }
    doc.setFontSize(11);
    doc.text(
      `${t("statusLabel", "Status")}: ${String(match.status || "")}`,
      marginX,
      70,
    );

    let y = 104;
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(t("analytics.teamComparison", "Team Comparison"), marginX, y);

    autoTable(doc, {
      startY: y + 8,
      head: [["Metric", match.home_team.name, match.away_team.name]],
      // PMA-COCK-098 follow-up (2026-08-06, lead decision (c)): the xG row
      // added to the on-screen comparison table is deliberately excluded
      // from THIS export specifically — the dedicated "Advanced Metrics
      // (xG)" table below already carried xG before this wave, and
      // exporting the same figure twice in one client-facing PDF reads as
      // a bug even when the numbers agree. Screen behavior is unaffected;
      // only this PDF body filters the row out.
      body: analytics.comparativeRows
        .filter((row) => row.testId !== "stat-xg")
        .map((row) => {
          const pdfCell = (
            value: string | number,
            side: "home" | "away",
          ): string =>
            row.cardBadges
              ? `${value} (${t("analytics.yellowCards", "Yellow Cards")}: ${row.cardBadges[side].yellow}, ${t("analytics.redCards", "Red Cards")}: ${row.cardBadges[side].red})`
              : String(value);
          return [
            row.label,
            pdfCell(row.home, "home"),
            pdfCell(row.away, "away"),
          ];
        }),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 5, lineColor: [226, 232, 240] },
      headStyles: {
        fillColor: [16, 185, 129],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: marginX, right: marginX },
    });

    if (advancedMetrics?.team_totals) {
      y = ((doc as any).lastAutoTable?.finalY || y + 120) + 22;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(
        t("advancedMetrics.pdfTitle", "Advanced Metrics (xG)"),
        marginX,
        y,
      );
      autoTable(doc, {
        startY: y + 8,
        head: [["Metric", match.home_team.name, match.away_team.name]],
        body: [
          [
            t("advancedMetrics.xgFull", "Expected Goals (xG)"),
            advancedMetrics.team_totals.home.xg.toFixed(2),
            advancedMetrics.team_totals.away.xg.toFixed(2),
          ],
          // Progressive Passes row hidden per client request (PMA-COCK-160,
          // client wave 2026-09-01) — same precedent as Progressive Carries
          // below: data/computation
          // (advancedMetrics.team_totals.*.progressive_passes) and the
          // advancedMetrics.progressivePassesFull i18n key stay intact.
          // Progressive Carries row hidden pending client release (PMA-COCK).
          // Data/computation (advancedMetrics.team_totals.*.progressive_carries)
          // and the advancedMetrics.progressiveCarriesFull i18n key stay intact.
        ],
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 5, lineColor: [226, 232, 240] },
        headStyles: {
          fillColor: [99, 102, 241],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: marginX, right: marginX },
      });
    }

    // Per-Team Time Detail table removed from the PDF export per client
    // request (PMA-COCK-160, client wave 2026-09-01). The on-screen table
    // (`analytics-per-team-time`, gated by the pre-existing
    // `hidePerTeamTimeTable` prop) and its `perTeamTimeRows` data are
    // untouched — display-only, export-only removal.

    // PMA-COCK-189 (client wave 2026-09-13): the Ineffective Breakdown
    // title + autoTable used to render here, right after the header
    // tables and ahead of every captured image section below. The client
    // asked for the "Tiempo perdido" block to move to the very end on
    // both screens and in the PDF ("se podria dejar de ultimo? ... igual
    // en el PDF") — the same title + autoTable, unchanged, now renders
    // after every `drawCapturedSection` call; see the bottom of this
    // function.

    // Event Type Distribution table removed from the PDF export per client
    // request (PMA-COCK-160, client wave 2026-09-01). The underlying
    // `analytics.eventTypes` computation is untouched — it had no on-screen
    // consumer either, this was its only render target.

    // ── Ball Recovery Time / Match Momentum / per-period maps / Area Control
    // / Field Heat Maps / Team Control Map captures ──
    // (PMA-COCK-160, order extended PMA-COCK-189) Mirrors the share page's
    // own section order — the share page has no export controls at all
    // (PMA-COCK-092), so this export is the only place these surfaces ever
    // reach a client-facing document, and it should read like an export of
    // what the share page already shows. Each capture reuses the exact
    // widen/restore/page-break handling below.
    const EXPORT_CAPTURE_WIDTH = 1100;

    // PMA-COCK-170 (client wave 2026-09-07): the Area Control legend
    // ("revisar porque sale un poco cortado") joins the un-truncate set
    // alongside the pre-existing `.truncate`/`overflow-hidden` sweep, keyed
    // by its own `-legend` testid suffix (`AreaControlGrid.tsx`) rather than
    // a class, since the legend itself carries neither truncation class.
    const UN_TRUNCATE_SELECTOR =
      ".truncate, [class*='overflow-hidden'], [data-testid$='-legend']";
    // Elements the client asked cut from the PDF export only — the
    // "¿Cómo se calcula?" toggles and the momentum fallback footnote — never
    // touched on screen, only hidden inside html2canvas's cloned document.
    const EXPORT_HIDDEN_SELECTOR = "[data-export-hidden]";

    const captureSelectorAsImage = async (
      selector: string,
    ): Promise<{ dataUrl: string; width: number; height: number } | null> => {
      const el = document.querySelector<HTMLElement>(selector);
      if (!el) return null;

      const prevW = el.style.minWidth;
      const prevMaxW = el.style.maxWidth;
      const prevOv = el.style.overflow;
      el.style.minWidth = `${EXPORT_CAPTURE_WIDTH}px`;
      el.style.maxWidth = `${EXPORT_CAPTURE_WIDTH}px`;
      el.style.overflow = "visible";
      const restored: {
        el: HTMLElement;
        ov: string;
        ws: string;
        to: string;
      }[] = [];
      el.querySelectorAll<HTMLElement>(UN_TRUNCATE_SELECTOR).forEach(
        (child) => {
          restored.push({
            el: child,
            ov: child.style.overflow,
            ws: child.style.whiteSpace,
            to: child.style.textOverflow,
          });
          child.style.overflow = "visible";
          child.style.whiteSpace = "normal";
          child.style.textOverflow = "clip";
        },
      );

      const canvas = await html2canvas(el, {
        backgroundColor: "#0f172a",
        scale: 2,
        windowWidth: EXPORT_CAPTURE_WIDTH,
        useCORS: true,
        onclone: (clonedDoc: Document) => {
          clonedDoc
            .querySelectorAll<HTMLElement>(EXPORT_HIDDEN_SELECTOR)
            .forEach((hidden) => {
              hidden.style.display = "none";
            });
        },
      });

      el.style.minWidth = prevW;
      el.style.maxWidth = prevMaxW;
      el.style.overflow = prevOv;
      restored.forEach(({ el: c, ov, ws, to }) => {
        c.style.overflow = ov;
        c.style.whiteSpace = ws;
        c.style.textOverflow = to;
      });

      return {
        dataUrl: canvas.toDataURL("image/png"),
        width: canvas.width,
        height: canvas.height,
      };
    };

    const drawCapturedSection = async (
      selector: string,
      titleKey: string,
      titleFallback: string,
    ) => {
      const capture = await captureSelectorAsImage(selector);
      if (!capture) return;
      const imgW = pageWidth - marginX * 2;
      const imgH = (capture.height / capture.width) * imgW;
      const pageH = doc.internal.pageSize.getHeight();
      if (y + imgH + 30 > pageH) {
        doc.addPage();
        y = 36;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(t(titleKey, titleFallback), marginX, y);
      doc.addImage(capture.dataUrl, "PNG", marginX, y + 10, imgW, imgH);
      // Advance past this image for whichever section comes next — no
      // autoTable call happens between here and the next section any more,
      // so `doc.lastAutoTable.finalY` can't be relied on to position it.
      y = y + 10 + imgH + 22;
    };

    y = ((doc as any).lastAutoTable?.finalY || y + 120) + 22;

    await drawCapturedSection(
      '[data-testid="recovery-banner"]',
      "analytics.recoveryTitle",
      "Ball Recovery Time",
    );
    await drawCapturedSection(
      '[data-testid="momentum-card"]',
      "analytics.momentumTitle",
      "Match Momentum",
    );

    // PMA-COCK-189: one capture per period the cockpit now renders on
    // screen (`PeriodMapsSection`, PMA-COCK-188), inserted here — after
    // Recovery/Momentum, before the whole-match ("Generales") captures below
    // — matching the on-screen order (period blocks first, general maps
    // last). Real, already-visible DOM nodes captured the same way as every
    // other section here, so no new offscreen mount is introduced.
    // `getRenderedPeriods` is the exact same helper both on-screen mount
    // sites (`AnalyticsView`, `PublicMatchAnalytics`) use to decide which
    // blocks exist, so the PDF can never disagree with either screen about
    // which periods appear.
    for (const period of getRenderedPeriods(events)) {
      const label = PERIOD_MAPS_LABEL_KEYS[period];
      await drawCapturedSection(
        `[data-testid="period-maps-section-${period}"]`,
        label.key,
        label.fallback,
      );
    }

    // Area Control isn't mounted by the cockpit's default per-player
    // heat-map view (`HeatMapSection`'s sibling `view` state defaults to
    // "perPlayer"), so it's captured via the offscreen mount below instead
    // of a node that usually isn't in the document — the visible cockpit UI
    // (including the real heat-map view toggle) is never touched.
    setIsCapturingAreaControl(true);
    await waitForNextFrame();
    await drawCapturedSection(
      '[data-testid="area-control-export-capture"]',
      "analytics.areaControl",
      "Area Control",
    );
    setIsCapturingAreaControl(false);

    // ── Heat maps (general, whole-match) ──
    // Refactored onto the shared captureSelectorAsImage/drawCapturedSection
    // helpers above (PMA-COCK-170) — was a hand-rolled duplicate of the same
    // widen/un-truncate/restore logic, which would otherwise have needed its
    // own copy of the `onclone` [data-export-hidden] hiding added for this
    // UC. The live `heatmap-section` node now always carries a
    // `data-export-hidden` `TeamControlMap` embed (`HeatMapSection.tsx`, both
    // toggle states) — hidden here so it never doubles up with this export's
    // own dedicated "Mapa de control por equipo" section below. Behavior
    // otherwise unchanged: same 1100px capture width, same un-truncate sweep,
    // same page-break math, same `y` advance, same relative order versus
    // Team Control Map below (PMA-COCK-170 — NOT reordered by PMA-COCK-189).
    await drawCapturedSection(
      '[data-testid="heatmap-section"]',
      "analytics.heatMaps",
      "Field Heat Maps",
    );

    // PMA-COCK-170 (client wave 2026-09-07): "en el pdf ... agregaría lo
    // nuevo de mapas de control por equipo" — a dedicated section after the
    // heat maps, same offscreen-mount pattern as Area Control above (the
    // live `HeatMapSection` embed is intentionally excluded from the "Field
    // Heat Maps" capture just above via `data-export-hidden`). PMA-COCK-189
    // does not move this — only the new per-period captures (above) and the
    // Ineffective Breakdown table's final position (below) are new; the
    // whole-match capture order shipped under PMA-COCK-170 is unchanged.
    setIsCapturingControlMap(true);
    await waitForNextFrame();
    await drawCapturedSection(
      '[data-testid="team-control-map-export-capture"]',
      "analytics.controlMapTitle",
      "Team Control Map",
    );
    setIsCapturingControlMap(false);

    // PMA-COCK-189: Ineffective Breakdown moves to the very end of the
    // document, after every drawCapturedSection call above — same title +
    // autoTable content as before, only its position changed. `y` is
    // whatever the last successful image capture advanced it to (each
    // `drawCapturedSection` call leaves a trailing gap already baked in),
    // so no `lastAutoTable.finalY` fallback is needed here the way the
    // pre-image-capture tables above still use one.
    y = y + 22;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(
      t("analytics.ineffectiveBreakdown", "Ineffective Breakdown"),
      marginX,
      y,
    );

    autoTable(doc, {
      startY: y + 8,
      head: [
        [
          "Metric",
          match.home_team.name,
          t("analytics.neutral", "Neutral"),
          match.away_team.name,
        ],
      ],
      body: analytics.ineffectiveActionRows.map((row: any) => [
        row.label,
        formatSecondsAsClock(row.home),
        formatSecondsAsClock(row.neutral),
        formatSecondsAsClock(row.away),
      ]),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 5, lineColor: [226, 232, 240] },
      headStyles: {
        fillColor: [14, 116, 144],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: marginX, right: marginX },
    });

    // ── Footer on last page ──
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `${t(
        "analytics.realtime",
        "Real-time data visualization",
      )} • ${new Date().toLocaleString()}`,
      marginX,
      doc.internal.pageSize.getHeight() - 18,
    );

    // ── Repeating watermark on every page ──
    await drawPdfWatermark(doc);

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-");
    doc.save(`analytics-${match.id || "match"}-${timestamp}.pdf`);
  };

  const homeTeamLabel = match.home_team.short_name || match.home_team.name;
  const awayTeamLabel = match.away_team.short_name || match.away_team.name;
  const {
    home: homeTeamColor,
    away: awayTeamColor,
    homeSecondary: homeTeamSecondaryColor,
    awaySecondary: awayTeamSecondaryColor,
  } = resolveMatchTeamColors(match);
  return (
    <div className="space-y-6 pb-6" data-testid="analytics-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2
          className="text-2xl font-bold text-slate-100 flex items-center gap-2"
          data-testid="analytics-title"
        >
          <TrendingUp className="text-purple-600" />
          {t("analytics.title", "Live Match Analytics")}
        </h2>
        {!hideExportControls && (
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {t("analytics.totalEvents", "Total Events")}:{" "}
              <span data-testid="analytics-total-events">
                {analytics.totalEvents}
              </span>
            </div>
            <div className="text-sm text-slate-400">
              {t("analytics.realtime", "Real-time data visualization")}
            </div>
            <button
              type="button"
              data-testid="export-analytics-pdf"
              onClick={exportPdf}
              className="px-3 py-1.5 min-h-11 rounded-md text-xs font-semibold bg-slate-700 text-white hover:bg-slate-800"
            >
              {t("analytics.exportPdf", "Export PDF")}
            </button>
          </div>
        )}
      </div>

      {/* Comparative Table */}
      <div
        className={`rounded-2xl p-4 sm:p-5 md:p-6 shadow-xl ${ACTIVE_STATS_THEME.panel}`}
      >
        {gate(
          "stats_comparison",
          <>
            <div className="flex items-center justify-between mb-4">
              <div
                className={`text-base uppercase tracking-wider ${ACTIVE_STATS_THEME.label}`}
              >
                {t("analytics.totalTime", "Total Time")}:
              </div>
              <div
                className={`text-xl font-mono font-semibold ${ACTIVE_STATS_THEME.valueAlt}`}
              >
                {formatSecondsAsClock(analytics.totalMatchSeconds)}
              </div>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 md:gap-4 mb-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <TeamLogo team={match.home_team} size="xl" bare />
                  <span
                    className={`text-3xl md:text-4xl font-bold tabular-nums ${ACTIVE_STATS_THEME.valueAlt}`}
                    data-testid="analytics-score-home"
                  >
                    {analytics.homeScore}
                  </span>
                </div>
                <div
                  className="text-sm md:text-base font-semibold truncate"
                  title={homeTeamLabel}
                >
                  {homeTeamLabel}
                </div>
                <div
                  className="relative h-16 w-16"
                  data-testid="analytics-possession-home"
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `conic-gradient(${homeTeamColor} ${analytics.homePossession}%, ${ACTIVE_STATS_THEME.donutTrack} 0)`,
                    }}
                  />
                  <div
                    className={`absolute inset-2 rounded-full flex items-center justify-center text-base font-bold ${ACTIVE_STATS_THEME.donutInset}`}
                  >
                    {analytics.homePossession}%
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div
                  className={`text-sm md:text-base font-semibold uppercase tracking-wide ${ACTIVE_STATS_THEME.label}`}
                >
                  VS
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-3">
                  <span
                    className={`text-3xl md:text-4xl font-bold tabular-nums ${ACTIVE_STATS_THEME.valueAlt}`}
                    data-testid="analytics-score-away"
                  >
                    {analytics.awayScore}
                  </span>
                  <TeamLogo team={match.away_team} size="xl" bare />
                </div>
                <div
                  className="text-sm md:text-base font-semibold truncate"
                  title={awayTeamLabel}
                >
                  {awayTeamLabel}
                </div>
                <div
                  className="relative h-16 w-16"
                  data-testid="analytics-possession-away"
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `conic-gradient(${awayTeamColor} ${analytics.awayPossession}%, ${ACTIVE_STATS_THEME.donutTrack} 0)`,
                    }}
                  />
                  <div
                    className={`absolute inset-2 rounded-full flex items-center justify-center text-base font-bold ${ACTIVE_STATS_THEME.donutInset}`}
                  >
                    {analytics.awayPossession}%
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`grid grid-cols-[minmax(0,1fr)_minmax(104px,150px)_minmax(0,1fr)] md:grid-cols-[minmax(80px,1fr)_minmax(150px,190px)_minmax(80px,1fr)] text-sm md:text-base uppercase tracking-wide mb-2 gap-2 ${ACTIVE_STATS_THEME.label}`}
            >
              <div className="truncate" data-testid="analytics-shortname-home">
                {homeTeamLabel}
              </div>
              <div className="text-center truncate">
                {t("analytics.metric", "Metric")}
              </div>
              <div
                className="text-right truncate"
                data-testid="analytics-shortname-away"
              >
                {awayTeamLabel}
              </div>
            </div>

            <div
              className="space-y-2 overflow-x-clip"
              data-testid="analytics-comparison-table"
            >
              {analytics.comparativeRows.map((row, idx) => (
                <div
                  key={row.testId}
                  className={`grid grid-cols-[minmax(0,1fr)_minmax(104px,150px)_minmax(0,1fr)] md:grid-cols-[minmax(80px,1fr)_minmax(150px,190px)_minmax(80px,1fr)] items-center gap-2 md:gap-3 rounded-lg px-2.5 md:px-3 py-2.5 md:py-3 ${
                    ACTIVE_STATS_THEME.row
                  } ${
                    idx < analytics.comparativeRows.length - 1
                      ? `border-b ${ACTIVE_STATS_THEME.border}`
                      : ""
                  }`}
                  data-testid={row.testId}
                  {...row.dataAttrs}
                >
                  {row.cardBadges ? (
                    <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                      <div
                        className={`text-base sm:text-lg md:text-xl font-semibold ${ACTIVE_STATS_THEME.value}`}
                        data-testid="stat-home-value"
                      >
                        {row.home}
                      </div>
                      <CardBadge
                        kind="yellow"
                        side="home"
                        count={row.cardBadges.home.yellow}
                        label={t("analytics.yellowCards", "Yellow Cards")}
                      />
                      <CardBadge
                        kind="red"
                        side="home"
                        count={row.cardBadges.home.red}
                        label={t("analytics.redCards", "Red Cards")}
                      />
                    </div>
                  ) : (
                    <div
                      className={`text-base sm:text-lg md:text-xl font-semibold ${ACTIVE_STATS_THEME.value}`}
                      data-testid="stat-home-value"
                    >
                      {row.home}
                    </div>
                  )}
                  <div
                    className={`text-sm sm:text-base md:text-lg text-center font-medium leading-tight px-1 flex items-center justify-center gap-1 ${ACTIVE_STATS_THEME.label}`}
                    title={row.label}
                  >
                    <span className="break-words">{row.label}</span>
                    {row.tooltip && (
                      <span className="group relative flex-shrink-0">
                        <Info
                          size={14}
                          className={`cursor-help ${ACTIVE_STATS_THEME.icon}`}
                        />
                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 rounded-md bg-slate-800 px-3 py-2 text-xs leading-relaxed text-slate-200 opacity-0 shadow-lg transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 z-50 normal-case tracking-normal text-left">
                          {row.tooltip}
                        </span>
                      </span>
                    )}
                  </div>
                  {row.cardBadges ? (
                    <div className="flex items-center justify-end gap-1.5 md:gap-2 min-w-0">
                      <CardBadge
                        kind="red"
                        side="away"
                        count={row.cardBadges.away.red}
                        label={t("analytics.redCards", "Red Cards")}
                      />
                      <CardBadge
                        kind="yellow"
                        side="away"
                        count={row.cardBadges.away.yellow}
                        label={t("analytics.yellowCards", "Yellow Cards")}
                      />
                      <div
                        className={`text-base sm:text-lg md:text-xl font-semibold text-right ${ACTIVE_STATS_THEME.value}`}
                        data-testid="stat-away-value"
                      >
                        {row.away}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`text-base sm:text-lg md:text-xl font-semibold text-right ${ACTIVE_STATS_THEME.value}`}
                      data-testid="stat-away-value"
                    >
                      {row.away}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>,
        )}

        {topPlayersByTeam &&
          gate(
            "top_players",
            <TopPlayersSection
              homeName={homeTeamLabel}
              awayName={awayTeamLabel}
              home={topPlayersByTeam.home}
              away={topPlayersByTeam.away}
              homeTeam={match.home_team}
              awayTeam={match.away_team}
              theme={ACTIVE_STATS_THEME}
              t={t}
            />,
          )}

        {showPlayerCard &&
          rosterReady &&
          gate(
            "player_card",
            <div data-testid="public-player-card-section">
              <PlayerCardPicker
                match={match}
                playerStats={playerStats}
                events={events}
                currentClockSeconds={currentClockSeconds ?? 0}
                currentPeriod={currentPeriod}
                matchFinished={matchFinished}
                t={t}
              />
            </div>,
          )}

        {gate(
          "shot_map",
          <>
            {/* PMA-COCK-182: Goal mouth — team + optional per-player placement maps */}
            <div
              className="mt-6 space-y-2"
              data-testid="analytics-goal-mouth-section"
            >
              <div
                className={`text-sm uppercase tracking-wide mb-2 ${ACTIVE_STATS_THEME.label}`}
              >
                {t("analytics.goalMouth", "Shot map")}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* PMA-COCK-193: no team colour passed — GoalMouthMap now
                    paints every marker/legend swatch from its own fixed
                    per-outcome palette. */}
                <GoalMouthMap
                  events={events}
                  team={match.home_team}
                  side="home"
                  hidePlayerSelector={hidePlayerHeatmaps}
                  t={t}
                />
                <GoalMouthMap
                  events={events}
                  team={match.away_team}
                  side="away"
                  hidePlayerSelector={hidePlayerHeatmaps}
                  t={t}
                />
              </div>
            </div>
          </>,
        )}

        {/* Per-team Time Detail */}
        {!hidePerTeamTimeTable && (
          <div className="mt-6" data-testid="analytics-per-team-time">
            <div
              className={`text-sm uppercase tracking-wide mb-2 ${ACTIVE_STATS_THEME.label}`}
            >
              {t("analytics.perTeamTime", "Per-Team Time Detail")}
            </div>
            <div
              className={`grid grid-cols-[minmax(0,1fr)_minmax(104px,150px)_minmax(0,1fr)] md:grid-cols-[minmax(80px,1fr)_minmax(150px,190px)_minmax(80px,1fr)] text-sm md:text-base uppercase tracking-wide mb-2 gap-2 ${ACTIVE_STATS_THEME.label}`}
            >
              <div className="truncate">{match.home_team.short_name}</div>
              <div className="text-center truncate">
                {t("analytics.metric", "Metric")}
              </div>
              <div className="text-right truncate">
                {match.away_team.short_name}
              </div>
            </div>
            <div className="space-y-2 overflow-x-clip">
              {analytics.perTeamTimeRows.map((row, idx) => (
                <div
                  key={row.testId}
                  className={`grid grid-cols-[minmax(0,1fr)_minmax(104px,150px)_minmax(0,1fr)] md:grid-cols-[minmax(80px,1fr)_minmax(150px,190px)_minmax(80px,1fr)] items-center gap-2 md:gap-3 rounded-lg px-2.5 md:px-3 py-2.5 md:py-3 ${
                    ACTIVE_STATS_THEME.row
                  } ${
                    idx < analytics.perTeamTimeRows.length - 1
                      ? `border-b ${ACTIVE_STATS_THEME.border}`
                      : ""
                  }`}
                  data-testid={row.testId}
                >
                  {row.cardBadges ? (
                    <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                      <div
                        className={`text-base sm:text-lg md:text-xl font-semibold ${ACTIVE_STATS_THEME.value}`}
                        data-testid="stat-home-value"
                      >
                        {row.home}
                      </div>
                      <CardBadge
                        kind="yellow"
                        side="home"
                        count={row.cardBadges.home.yellow}
                        label={t("analytics.yellowCards", "Yellow Cards")}
                      />
                      <CardBadge
                        kind="red"
                        side="home"
                        count={row.cardBadges.home.red}
                        label={t("analytics.redCards", "Red Cards")}
                      />
                    </div>
                  ) : (
                    <div
                      className={`text-base sm:text-lg md:text-xl font-semibold ${ACTIVE_STATS_THEME.value}`}
                      data-testid="stat-home-value"
                    >
                      {row.home}
                    </div>
                  )}
                  <div
                    className={`text-sm sm:text-base md:text-lg text-center font-medium leading-tight px-1 flex items-center justify-center gap-1 ${ACTIVE_STATS_THEME.label}`}
                    title={row.label}
                  >
                    <span className="break-words">{row.label}</span>
                    {row.tooltip && (
                      <span className="group relative flex-shrink-0">
                        <Info
                          size={14}
                          className={`cursor-help ${ACTIVE_STATS_THEME.icon}`}
                        />
                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 rounded-md bg-slate-800 px-3 py-2 text-xs leading-relaxed text-slate-200 opacity-0 shadow-lg transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 z-50 normal-case tracking-normal text-left">
                          {row.tooltip}
                        </span>
                      </span>
                    )}
                  </div>
                  {row.cardBadges ? (
                    <div className="flex items-center justify-end gap-1.5 md:gap-2 min-w-0">
                      <CardBadge
                        kind="red"
                        side="away"
                        count={row.cardBadges.away.red}
                        label={t("analytics.redCards", "Red Cards")}
                      />
                      <CardBadge
                        kind="yellow"
                        side="away"
                        count={row.cardBadges.away.yellow}
                        label={t("analytics.yellowCards", "Yellow Cards")}
                      />
                      <div
                        className={`text-base sm:text-lg md:text-xl font-semibold text-right ${ACTIVE_STATS_THEME.value}`}
                        data-testid="stat-away-value"
                      >
                        {row.away}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`text-base sm:text-lg md:text-xl font-semibold text-right ${ACTIVE_STATS_THEME.value}`}
                      data-testid="stat-away-value"
                    >
                      {row.away}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {match &&
        gate(
          "recovery_banner",
          // The `recovery-banner` wrapper testid is new (PMA-COCK-160) — added
          // here, not inside `BallRecoveryTimeBanner` itself, purely so
          // `exportPdf` has a stable capture target; the banner's own internal
          // markup/testids are unchanged.
          <div data-testid="recovery-banner">
            <BallRecoveryTimeBanner
              events={events}
              homeTeamId={match.home_team.id}
              awayTeamId={match.away_team.id}
              homeLabel={homeTeamLabel}
              awayLabel={awayTeamLabel}
              homeTeamColor={homeTeamColor}
              awayTeamColor={awayTeamColor}
              homeTeamSecondaryColor={homeTeamSecondaryColor}
              awayTeamSecondaryColor={awayTeamSecondaryColor}
              homeTeam={match.home_team}
              awayTeam={match.away_team}
              t={t}
            />
          </div>,
        )}
      {match &&
        gate(
          "momentum_chart",
          <MatchMomentumChart
            events={events}
            homeTeamId={match.home_team.id}
            awayTeamId={match.away_team.id}
            homeLabel={homeTeamLabel}
            awayLabel={awayTeamLabel}
            homeTeamColor={homeTeamColor}
            awayTeamColor={awayTeamColor}
            homeTeamSecondaryColor={homeTeamSecondaryColor}
            awayTeamSecondaryColor={awayTeamSecondaryColor}
            homeTeam={match.home_team}
            awayTeam={match.away_team}
            t={t}
          />,
        )}
      {isCapturingAreaControl && match && (
        // PMA-COCK-160 capture-only offscreen mount: fed the same
        // `computeAreaControlData` input `HeatMapSection` uses for its own
        // (usually-not-mounted-in-perPlayer-view) Area Control grid, so the
        // PDF export never depends on the cockpit's own heat-map view state.
        // Positioned off-viewport and non-interactive — never visible, never
        // part of the normal render tree except for the brief span of
        // `exportPdf`'s Area Control capture step.
        <div
          data-testid="area-control-export-capture"
          aria-hidden="true"
          style={{
            position: "fixed",
            top: 0,
            left: "-10000px",
            width: "1100px",
            // PMA-COCK-170: p-4 (16px) so the legend has room to breathe at
            // capture width instead of sitting flush against the crop edge.
            padding: "16px",
            pointerEvents: "none",
          }}
        >
          <AreaControlGrid
            data={computeAreaControlData(
              events,
              match.home_team.id,
              match.away_team.id,
            )}
            homeTeamName={homeTeamLabel}
            awayTeamName={awayTeamLabel}
            homeColor={homeTeamColor}
            awayColor={awayTeamColor}
            homeTeam={match.home_team}
            awayTeam={match.away_team}
            t={t}
            // Own scoped testid (distinct from HeatMapSection's real grid,
            // which uses "area-control-grid") so this offscreen capture's
            // crest/zone/svg testids never collide with the cockpit's own
            // grid when both happen to be mounted at once (PMA-COCK-161).
            data-testid="area-control-export-capture-grid"
          />
        </div>
      )}
      {isCapturingControlMap && match && (
        // PMA-COCK-170 capture-only offscreen mount, same pattern as the
        // Area Control one above: positioned off-viewport and
        // non-interactive, only present for the brief span of `exportPdf`'s
        // "Mapa de control por equipo" capture step. TeamControlMap computes
        // its own data straight from `events` (unlike AreaControlGrid, which
        // needs precomputed `data`), so no extra wiring is needed here.
        <div
          data-testid="team-control-map-export-capture"
          data-layout="team-rows"
          aria-hidden="true"
          style={{
            position: "fixed",
            top: 0,
            left: "-10000px",
            width: "1100px",
            padding: "16px",
            pointerEvents: "none",
          }}
        >
          <TeamControlMap
            match={match}
            events={events}
            t={t}
            data-testid="team-control-map-export-capture-grid"
            // EPIC-119 D6: same team-row layout as the screen (one row per
            // team, row-layout cards, no zones/lanes caption lines).
            renderCards={({ cards }) => (
              <div className="flex flex-col gap-4">
                {(["home", "away"] as const).map((side) => (
                  <div
                    key={side}
                    data-testid={`team-control-map-export-capture-row-${side}`}
                  >
                    {cards[side]}
                  </div>
                ))}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
